import React from "react";
import { useSearchParams } from "react-router-dom";
import EmptyState from "components/ui/EmptyState";
import SectionHeader from "components/ui/SectionHeader";
import StatusBadge from "components/ui/StatusBadge";
import ActionButton from "components/ui/ActionButton";
import {
  MdOutlineFactCheck,
  MdChevronRight,
  MdOutlineRemoveRedEye,
} from "react-icons/md";
import { useLanguage } from "context/LanguageContext";
import { authService } from "services/authService";
import {
  userPortalService,
  AttendanceResponse,
} from "services/userPortalService";
import AttendanceDetailModal from "./AttendanceDetailModal";

const PAGE_SIZE = 20;

type HistoryTab = "month" | "all";

interface MonthGroup {
  year: number;
  month: number;
  items: AttendanceResponse[];
}

function methodTone(method: string | null | undefined): "info" | "neutral" {
  return method === "FACE" || method === "face" ? "info" : "neutral";
}

function statusTone(
  status: string | null | undefined
): "success" | "warning" | "danger" | "neutral" {
  switch (status) {
    case "checked_in":
    case "checked_out":
      return "success";
    case "absent":
      return "danger";
    case "excused_absence":
      return "warning";
    default:
      return "neutral";
  }
}

/**
 * Student-facing equivalent of the QLL/HLV timesheet report
 * (views/admin/participants/StaffAttendance.tsx) — same visual language
 * (summary card + row list with status badges) but scoped to a single
 * person (the logged-in student) and sourced from /attendance/my instead
 * of the admin timesheet endpoints. No Excel export.
 */
const AttendanceHistory = () => {
  const { t } = useLanguage();
  const currentUser = authService.getStoredUser();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("sessionId");
  const [items, setItems] = React.useState<AttendanceResponse[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedRecord, setSelectedRecord] =
    React.useState<AttendanceResponse | null>(null);
  const [sessionRecordNotFound, setSessionRecordNotFound] =
    React.useState(false);
  const [activeTab, setActiveTab] = React.useState<HistoryTab>("month");
  const [sortDir, setSortDir] = React.useState<"desc" | "asc">("desc");
  const now = React.useRef(new Date()).current;
  const [selectedMonthKey, setSelectedMonthKey] = React.useState(
    `${now.getFullYear()}-${now.getMonth() + 1}`
  );

  const fetchAttendance = React.useCallback(
    (skip: number) => {
      const setBusy = skip === 0 ? setLoading : setLoadingMore;
      setBusy(true);
      setError(null);
      return userPortalService
        .getMyAttendance({ skip, limit: PAGE_SIZE })
        .then((res) => {
          setItems((prev) =>
            skip === 0 ? res.items : [...prev, ...res.items]
          );
          setTotal(res.total);
        })
        .catch((err: Error) => {
          setError(err.message || t("attendanceHistory.loadError"));
        })
        .finally(() => setBusy(false));
    },
    [t]
  );

  React.useEffect(() => {
    fetchAttendance(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-open the detail modal for a specific session, e.g. when arriving
  // here from a "check-in successful" notification click. The backend
  // supports filtering /attendance/my by session_id directly.
  React.useEffect(() => {
    if (!sessionId) return;
    userPortalService
      .getMyAttendance({ session_id: sessionId, limit: 1 })
      .then((res) => {
        const match = res.items[0];
        if (match) {
          setSelectedRecord(match);
        } else {
          setSessionRecordNotFound(true);
        }
      })
      .catch(() => {
        setSessionRecordNotFound(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const formatDate = (value: string | null | undefined) => {
    if (!value) return t("attendanceHistory.notAvailable");
    return new Date(value).toLocaleDateString("vi-VN");
  };

  const formatTime = (value: string | null | undefined) => {
    if (!value) return "--:--";
    return new Date(value).toLocaleTimeString("vi-VN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const sessionLabel = (sessionNumber: number | null | undefined) => {
    if (!sessionNumber) return null;
    return `${t("attendanceHistory.session")} ${sessionNumber}`;
  };

  const methodLabel = (method: string | null | undefined) => {
    if (!method) return t("attendanceHistory.notAvailable");
    if (method.toUpperCase() === "FACE")
      return t("attendanceHistory.methodFace");
    if (method.toUpperCase() === "MANUAL")
      return t("attendanceHistory.methodManual");
    return method;
  };

  const statusLabel = (status: string | null | undefined) => {
    switch (status) {
      case "checked_in":
        return t("attendanceHistory.statusCheckedIn");
      case "checked_out":
        return t("attendanceHistory.statusCheckedOut");
      case "absent":
        return t("attendanceHistory.statusAbsent");
      case "excused_absence":
        return t("attendanceHistory.statusExcused");
      default:
        return status || null;
    }
  };

  const canLoadMore = items.length < total;

  const summary = React.useMemo(() => {
    let checkedIn = 0;
    let absent = 0;
    let excused = 0;
    items.forEach((item) => {
      if (item.status === "checked_in" || item.status === "checked_out") {
        checkedIn += 1;
      } else if (item.status === "absent") {
        absent += 1;
      } else if (item.status === "excused_absence") {
        excused += 1;
      }
    });
    return { checkedIn, absent, excused };
  }, [items]);

  // Sort direction is a pure client-side re-sort of the already-loaded
  // `items` array — no new backend query param. Sorts by checked_in_at,
  // falling back to session_start_time when null (e.g. an absence record).
  const sortByDirection = React.useCallback(
    (list: AttendanceResponse[]) => {
      const withTime = (item: AttendanceResponse) => {
        const dateValue = item.checked_in_at || item.session_start_time;
        return dateValue ? new Date(dateValue).getTime() : 0;
      };
      return [...list].sort((a, b) =>
        sortDir === "desc"
          ? withTime(b) - withTime(a)
          : withTime(a) - withTime(b)
      );
    },
    [sortDir]
  );

  const sortedItems = React.useMemo(
    () => sortByDirection(items),
    [items, sortByDirection]
  );

  // Client-side grouping by month, derived from the same `items` array
  // used by the flat "all" tab — no separate backend query. Falls back to
  // session_start_time when checked_in_at is null (e.g. an absence record).
  // Month groups are always ordered newest-month-first; the row-level
  // `sortDir` toggle only affects the order of rows *within* each group.
  const monthGroups = React.useMemo<MonthGroup[]>(() => {
    const groups = new Map<string, MonthGroup>();
    items.forEach((item) => {
      const dateValue = item.checked_in_at || item.session_start_time;
      const parsed = dateValue ? new Date(dateValue) : null;
      const year = parsed ? parsed.getFullYear() : 0;
      const month = parsed ? parsed.getMonth() + 1 : 0;
      const key = `${year}-${month}`;
      const existing = groups.get(key);
      if (existing) {
        existing.items.push(item);
      } else {
        groups.set(key, { year, month, items: [item] });
      }
    });
    // Always include the current month as a selectable option even when no
    // record has been loaded for it yet, so the picker defaults sanely.
    const currentKey = `${now.getFullYear()}-${now.getMonth() + 1}`;
    if (!groups.has(currentKey)) {
      groups.set(currentKey, {
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        items: [],
      });
    }
    return Array.from(groups.values())
      .sort((a, b) => b.year - a.year || b.month - a.month)
      .map((group) => ({ ...group, items: sortByDirection(group.items) }));
  }, [items, sortByDirection, now]);

  const selectedMonthGroup = React.useMemo(
    () =>
      monthGroups.find(
        (group) => `${group.year}-${group.month}` === selectedMonthKey
      ) || null,
    [monthGroups, selectedMonthKey]
  );

  const renderViewAffordance = (item: AttendanceResponse) => {
    const hasPhoto = Boolean(
      item.check_in_snapshot_url || item.check_out_snapshot_url
    );
    return (
      <div className="flex shrink-0 items-center gap-1">
        {hasPhoto && (
          <MdOutlineRemoveRedEye
            title={t("attendanceHistory.hasPhoto")}
            className="h-4 w-4 text-gray-300 transition-colors group-hover:text-brand-500 dark:text-gray-600"
          />
        )}
        <MdChevronRight className="h-5 w-5 text-gray-300 transition-colors group-hover:text-brand-500 dark:text-gray-600" />
      </div>
    );
  };

  const renderAttendanceRow = (item: AttendanceResponse) => (
    <button
      type="button"
      key={item.id}
      onClick={() => setSelectedRecord(item)}
      className="group grid w-full grid-cols-[90px_minmax(0,1fr)_100px_100px_70px_70px_150px_20px] items-center gap-3 border-b border-line px-3 py-2.5 text-left transition-colors last:border-b-0 hover:bg-gray-50 dark:hover:bg-surface-hover/40"
    >
      <div className="text-sm font-semibold text-ink">
        {formatDate(item.checked_in_at || item.session_start_time)}
      </div>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-ink">
          {item.class_name || t("attendanceHistory.notAvailable")}
        </p>
        {item.venue_name && (
          <p className="truncate text-xs text-ink-subtle">
            {item.venue_name}
          </p>
        )}
      </div>
      <div className="min-w-0">
        <p className="truncate text-sm text-ink-muted">
          {sessionLabel(item.session_number) ||
            t("attendanceHistory.notAvailable")}
        </p>
        {item.session_title && (
          <p className="truncate text-xs text-ink-subtle">
            {item.session_title}
          </p>
        )}
      </div>
      <div className="flex min-w-0 items-center gap-2">
        <StatusBadge
          label={methodLabel(item.check_in_method)}
          tone={methodTone(item.check_in_method)}
          className="whitespace-nowrap px-2.5 py-1 text-[11px]"
        />
      </div>
      <div className="text-sm text-ink-muted">
        {formatTime(item.checked_in_at)}
      </div>
      <div className="text-sm text-ink-muted">
        {formatTime(item.checked_out_at)}
      </div>
      <div>
        {statusLabel(item.status) && (
          <StatusBadge
            label={statusLabel(item.status) as string}
            tone={statusTone(item.status)}
            className="whitespace-nowrap px-2.5 py-1 text-[11px]"
          />
        )}
      </div>
      {renderViewAffordance(item)}
    </button>
  );

  const renderAttendanceCard = (item: AttendanceResponse) => (
    <button
      type="button"
      key={item.id}
      onClick={() => setSelectedRecord(item)}
      aria-label={t("attendanceHistory.viewDetails")}
      className="group flex w-full flex-col gap-2 border-b border-line px-3 py-3 text-left transition-colors last:border-b-0 hover:bg-gray-50 dark:hover:bg-surface-hover/40"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
            {formatDate(item.checked_in_at || item.session_start_time)}
          </p>
          <p className="mt-0.5 truncate text-sm font-bold text-ink">
            {item.class_name || t("attendanceHistory.notAvailable")}
          </p>
          {item.venue_name && (
            <p className="truncate text-xs text-ink-subtle">
              {item.venue_name}
            </p>
          )}
          {sessionLabel(item.session_number) && (
            <p className="truncate text-xs font-semibold text-brand-500">
              {sessionLabel(item.session_number)}
            </p>
          )}
          {item.session_title && (
            <p className="truncate text-xs text-ink-subtle">
              {item.session_title}
            </p>
          )}
        </div>
        {renderViewAffordance(item)}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge
          label={methodLabel(item.check_in_method)}
          tone={methodTone(item.check_in_method)}
          className="px-2.5 py-1 text-[11px]"
        />
        {statusLabel(item.status) && (
          <StatusBadge
            label={statusLabel(item.status) as string}
            tone={statusTone(item.status)}
            className="px-2.5 py-1 text-[11px]"
          />
        )}
      </div>
      <div className="flex items-center gap-3 text-xs text-ink-muted">
        <span>
          {t("attendanceHistory.checkedInAt")}: {formatTime(item.checked_in_at)}
        </span>
        <span>
          {t("attendanceHistory.checkedOutAt")}:{" "}
          {formatTime(item.checked_out_at)}
        </span>
      </div>
    </button>
  );

  const renderAttendanceItem = (item: AttendanceResponse) => (
    <React.Fragment key={item.id}>
      <div className="hidden md:block">{renderAttendanceRow(item)}</div>
      <div className="flex flex-col md:hidden">
        {renderAttendanceCard(item)}
      </div>
    </React.Fragment>
  );

  return (
    <div className="mt-5 flex flex-col gap-5">
      <SectionHeader title={t("attendanceHistory.title")} className="!mb-0" />

      {error && (
        <div className="rounded-lg bg-red-50 p-4 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}

      {sessionRecordNotFound && (
        <div className="rounded-lg bg-amber-50 p-4 text-sm text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
          {t("attendanceHistory.recordNotFound")}
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-line bg-surface py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-500" />
          <p className="mt-3 text-sm text-ink-muted">
            {t("attendanceHistory.loading")}
          </p>
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<MdOutlineFactCheck className="h-10 w-10" />}
          title={t("attendanceHistory.empty")}
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
          {/* Summary header, mirrors the staff-timesheet single-person view */}
          <div className="border-b border-line bg-gradient-to-br from-brand-50 via-white to-blue-50/60 p-5 dark:from-brand-500/10 dark:via-navy-800 dark:to-navy-800">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex min-w-0 items-center gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-brand-600 text-xl font-bold text-white shadow-md ring-2 ring-white">
                  {(currentUser?.full_name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="truncate text-xl font-bold text-ink">
                    {currentUser?.full_name || "—"}
                  </p>
                  <p className="truncate text-xs text-ink-muted">
                    {currentUser?.email}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  {
                    key: "total",
                    label: t("attendanceHistory.totalSessions"),
                    value: String(total),
                    valueClassName: "text-ink",
                    borderClass: "border-line",
                  },
                  {
                    key: "checkedIn",
                    label: t("attendanceHistory.checkedInCount"),
                    value: String(summary.checkedIn),
                    valueClassName: "text-green-600 dark:text-green-300",
                    borderClass: "border-green-200 dark:border-green-500/30",
                  },
                  {
                    key: "absent",
                    label: t("attendanceHistory.absentCount"),
                    value: String(summary.absent),
                    valueClassName: "text-red-600 dark:text-red-300",
                    borderClass: "border-red-200 dark:border-red-500/30",
                  },
                ].map((stat) => (
                  <div
                    key={stat.key}
                    className={`min-w-[84px] rounded-lg border ${stat.borderClass} bg-white/85 px-2.5 py-1.5 text-center shadow-sm backdrop-blur dark:bg-surface-muted/60`}
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-muted">
                      {stat.label}
                    </p>
                    <p
                      className={`mt-0.5 text-lg font-bold leading-tight ${stat.valueClassName}`}
                    >
                      {stat.value}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Tabs + sort toggle */}
          <div className="flex flex-col gap-3 border-b border-line px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="inline-flex w-fit rounded-lg bg-surface-muted p-1">
              {(
                [
                  { key: "month", label: t("attendanceHistory.tabByMonth") },
                  { key: "all", label: t("attendanceHistory.tabAll") },
                ] as { key: HistoryTab; label: string }[]
              ).map((tabOption) => (
                <button
                  key={tabOption.key}
                  type="button"
                  onClick={() => setActiveTab(tabOption.key)}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                    activeTab === tabOption.key
                      ? "bg-white text-brand-600 shadow-sm dark:bg-surface-muted dark:text-white"
                      : "text-ink-muted hover:text-gray-700 dark:hover:text-gray-200"
                  }`}
                >
                  {tabOption.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              {activeTab === "month" && (
                <select
                  value={selectedMonthKey}
                  onChange={(e) => setSelectedMonthKey(e.target.value)}
                  className="rounded-lg border border-line bg-surface px-3 py-1.5 text-xs font-semibold text-ink shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                >
                  {monthGroups.map((group) => (
                    <option
                      key={`${group.year}-${group.month}`}
                      value={`${group.year}-${group.month}`}
                    >
                      {t("attendanceHistory.monthGroupPrefix")} {group.month}/
                      {group.year}
                    </option>
                  ))}
                </select>
              )}
              <div className="inline-flex w-fit rounded-lg bg-surface-muted p-1">
              {(
                [
                  { key: "desc", label: t("attendanceHistory.sortNewest") },
                  { key: "asc", label: t("attendanceHistory.sortOldest") },
                ] as { key: "desc" | "asc"; label: string }[]
              ).map((sortOption) => (
                <button
                  key={sortOption.key}
                  type="button"
                  onClick={() => setSortDir(sortOption.key)}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                    sortDir === sortOption.key
                      ? "bg-white text-brand-600 shadow-sm dark:bg-surface-muted dark:text-white"
                      : "text-ink-muted hover:text-gray-700 dark:hover:text-gray-200"
                  }`}
                >
                  {sortOption.label}
                </button>
              ))}
              </div>
            </div>
          </div>

          {/* Row list */}
          <div className="p-3">
            <div className="hidden grid-cols-[90px_minmax(0,1fr)_100px_100px_70px_70px_150px_20px] gap-3 border-b border-line bg-surface-muted px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-ink-muted md:grid">
              <span>{t("attendanceHistory.date")}</span>
              <span>{t("attendanceHistory.className")}</span>
              <span>{t("attendanceHistory.session")}</span>
              <span>{t("attendanceHistory.method")}</span>
              <span>{t("attendanceHistory.checkedInAt")}</span>
              <span>{t("attendanceHistory.checkedOutAt")}</span>
              <span>{t("attendanceHistory.status")}</span>
              <span />
            </div>

            {activeTab === "all" ? (
              sortedItems.map((item) => renderAttendanceItem(item))
            ) : selectedMonthGroup && selectedMonthGroup.items.length > 0 ? (
              selectedMonthGroup.items.map((item) => renderAttendanceItem(item))
            ) : (
              <div className="py-10 text-center text-sm text-ink-subtle">
                {t("attendanceHistory.emptyMonth")}
              </div>
            )}
          </div>

          {canLoadMore && (
            <div className="flex justify-center border-t border-line p-4">
              <ActionButton
                variant="secondary"
                size="sm"
                disabled={loadingMore}
                onClick={() => fetchAttendance(items.length)}
              >
                {loadingMore
                  ? t("attendanceHistory.loading")
                  : t("attendanceHistory.loadMore")}
              </ActionButton>
            </div>
          )}
        </div>
      )}

      {selectedRecord && (
        <AttendanceDetailModal
          record={selectedRecord}
          onClose={() => setSelectedRecord(null)}
        />
      )}
    </div>
  );
};

export default AttendanceHistory;
